import { ICreateTag, ITag } from "../../../interfaces/models/tag";
import { SidebarTypes } from "../../../interfaces/store/appStore";
import { ITagsFilterState, TTagsParametersState } from "../../../interfaces/store/tagStore";
import { useAuthStore } from "../../../stores/auth/useAuthStore";
import { useTagsActions } from "../../../stores/tags/useTagsActions";
import { useTagsStore } from "../../../stores/tags/useTagsStore";
import { ITagsQueryParams, useTagsRequest } from "../../apis/tags/useTagsRequest";
import { useWorkspaceRequest } from "../../apis/workspace/useWorkspaceRequest";
import { useAppHandlers } from "../app/useAppHandlers";
import { useConnectionsHandlers } from "../connections/useConnectionsHandlers";

export const useTagsHandlers = () => {

    const { getTagsPaginate, createTag, updateTag, deleteTag, deleteTags, updateTagsAccess } = useTagsRequest();
    const { setTags, setAllTags, setTagsFilter, setTagsParameters, resetTagsPagination, resetTagsStore } = useTagsActions()
    const { closeSidebar, setDestructiveModal, hideModal } = useAppHandlers()
    const { addTagToFilterOptions } = useConnectionsHandlers()
    const { store: { tagsFilter, tags, allTags, tagsParameters } } = useTagsStore()
    const { getWorkspaceUsersPublicAll} = useWorkspaceRequest()
    const { store: { workspace }} = useAuthStore()

    const filterTags = (tagList: ITag[]) =>
        workspace?.showAutoTags ? tagList : tagList.filter((tag: ITag) => !tag.isForbidden)

    const createTagHandler = async (
        payload: ICreateTag,
        displayToast: boolean = true,
        refresh: boolean = true
    ) => {
        const result = await createTag(payload, displayToast);
        if (result) {
            // Add the new tag to connection filter options immediately
            addTagToFilterOptions(result);
            setAllTags(filterTags([...allTags, result as unknown as ITag]));

            if (refresh) {
                await getTotalPaginatedTags();
            } else {
                // Optimistically add newly created tag to the store
                const newTags = [...tags, result as unknown as ITag];
                setTags(filterTags(newTags))
            }
            closeSidebar(SidebarTypes.TAG_EDIT_CREATE)
            return result
        }
    }

    const editTagHandler = async (id: string, payload: ICreateTag) => {
        const result = await updateTag(id, payload);
        if(result) {
            let newTags = tags.map((tag: ITag) => {
                if(tag._id === id) return ({ ...tag, ...payload})
                return tag
            })
            setTags(filterTags(newTags))
            setAllTags(filterTags(allTags.map((tag: ITag) =>
                tag._id === id ? ({ ...tag, ...payload }) : tag
            )))
            closeSidebar(SidebarTypes.TAG_EDIT_CREATE)
        }
    }

    const updateTagsAccessHandler = async (payload: any) => {
        const body = {
            ids: payload?.selectedTags,
            private: payload?.private
        }

        const result = await updateTagsAccess(body)

        if(result?.success) {
            let newTags = tags.map((tag: any) => {
                const updatedObj = payload?.selectedTags?.find((tagId: any) => tagId === tag._id)
                return updatedObj ? { ...tag, private: payload?.private } : tag
            })
            setTags(filterTags(newTags))
            setAllTags(filterTags(allTags.map((tag: any) => {
                const updatedObj = payload?.selectedTags?.find((tagId: any) => tagId === tag._id)
                return updatedObj ? { ...tag, private: payload?.private } : tag
            })))
        }
    }

    const deleteTagHandler = async (id: string) => {
        setDestructiveModal({
            headingText: "Delete tag",
            descriptionText: "Are you sure you want to delete this tag?",
            onCancelHandler: () => hideModal(),
            onConfirmHandler: () => {
                hideModal()
                deleteTagConfirmed(id)
            }
        })
    }

    const deleteTagConfirmed = async (id: string) => {
        const result = await deleteTag(id)
        if(result.success) {
            setAllTags(allTags.filter((tag: ITag) => tag._id !== id))
            await getTotalPaginatedTags()
        }
    }
    const deleteTagsHandler = async (ids: string[]) => {
        setDestructiveModal({
            headingText: "Delete tags",
            descriptionText: "Are you sure you want to delete selected tags?",
            onCancelHandler: () => hideModal(),
            onConfirmHandler: () => {
                hideModal()
                deleteTagsConfirmed(ids)
            }
        })
    }

    const deleteTagsConfirmed = async (ids: string[]) => {
        // Filter out kanban tags - they cannot be deleted
        const tagsToDelete = tags.filter((tag: ITag) => 
            ids.includes(tag._id) && !tag.forKanban
        ).map((tag: ITag) => tag._id);
        
        if (tagsToDelete.length === 0) {
            return; // No tags to delete (all were kanban tags)
        }
        
        const result = await deleteTags(tagsToDelete)
        if(result.success) {
            setAllTags(allTags.filter((tag: ITag) => !tagsToDelete.includes(tag._id)))
            await getTotalPaginatedTags()
        }
    }

    //Changed to paginate, we have to see if get tags is viable anywhere
    const mapTagsFilterToParams = (): ITagsQueryParams => ({
        page: tagsParameters?.page ?? 1,
        pageSize: tagsParameters?.pageSize ?? 25,
        searchByName: tagsFilter.searchByName || '',
        usersMutual: tagsFilter.selectedUsersMutual?.length
            ? tagsFilter.selectedUsersMutual.map((user: any) => user._id).join(',')
            : undefined,
        usersMutualCondition: tagsFilter.selectedUsersMutual?.length
            ? tagsFilter.usersMutualCondition
            : undefined,
        tagAccessType:
            tagsFilter.tagAccessType && (tagsFilter.tagAccessType as any)?.value !== 'all'
                ? (tagsFilter.tagAccessType as any).value
                : undefined,
        sort: tagsFilter.sort !== '' ? tagsFilter.sort : undefined,
    })

    const getTagsHandler = async (params: ITagsQueryParams) => {
        const result = await getTagsPaginate(params);
        if(result) {
            setTags(!result?.tags ? [] : filterTags(result.tags))
            setTagsParameters({ total: result?.metadata?.totalPaginated ?? 0 })
        }
        return result
    }

    /** Full tag list for modals/dropdowns — writes to allTags, never touches Tags-page list. */
    const loadAllTagsHandler = async () => {
        const result = await getTagsPaginate({ page: 1, pageSize: 1000, searchByName: '' });
        if (result) {
            setAllTags(!result?.tags ? [] : filterTags(result.tags))
        }
        return result
    }

    //there is no need to save total paginated in the store and since this has to be rewritten to be separate call we'll keep it like this
    const getTotalPaginatedTags = async () => {
        return await getTagsHandler(mapTagsFilterToParams());
    }

    const setTagsFilterHandler = async (payload: ITagsFilterState) => {
        setTagsFilter(payload);
    }

    const numberOfTagsFiltersHandler = () => {
        let numberOfFilters = 0
        if(tagsFilter.selectedUsersMutual?.length) numberOfFilters += 1
        if(tagsFilter.tagAccessType && tagsFilter.tagAccessType?.value !== 'all') numberOfFilters += 1
        return numberOfFilters
    }

    const setTagsParametersHandler = (payload: TTagsParametersState) => {
        setTagsParameters(payload)
    }

    const workspaceUsersPublicAllHandler = async (params: any) => {
        const { condition, name, email } = params

        const result = await getWorkspaceUsersPublicAll({ condition, name, email })
        return result ?? []
    }

    const setClearTagsFilterHandler = () => {
        resetTagsPagination();
    }

    const resetTagsStoreHandler = () => {
        resetTagsStore();
    }

    return {
        createTagHandler,
        getTagsHandler,
        loadAllTagsHandler,
        setTagsFilterHandler,
        editTagHandler,
        updateTagsAccessHandler,
        deleteTagHandler,
        deleteTagsHandler,
        getTotalPaginatedTags,
        numberOfTagsFiltersHandler,
        setTagsParametersHandler,
        workspaceUsersPublicAllHandler,
        setClearTagsFilterHandler,
        resetTagsStoreHandler
    };
}
